// ABOUTME: Heading of a node-info widget, built like the offer page's sidebar heading: a DS text back
// ABOUTME: button (chevron + label), the title as h6 and a divider; without a back action the label is overtext.
// ABOUTME: A separate backLabel (the page the detail was opened from) names the button, and the overtext then sits above the title.

import { Box, Button, ChevronLeftIcon, Divider, Stack, Typography } from '@ds/coolshop';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { SkeletonShape } from './WidgetSkeleton';

interface NodeInfoHeadingProps {
    title: string;
    /** The family or list the node belongs to: the back button's label, else the overtext. */
    overtext?: string;
    /**
     * Names the back button instead of the overtext (the page the detail was opened from); the
     * overtext is then shown as a caption above the title.
     */
    backLabel?: string;
    /** The title is still loading: a placeholder takes its line box. */
    titleLoading?: boolean;
    onBack?: () => void;
    endAdornment?: ReactNode;
}

export function NodeInfoHeading({
    title,
    overtext,
    backLabel,
    titleLoading = false,
    onBack,
    endAdornment,
}: NodeInfoHeadingProps) {
    const { formatMessage } = useIntl();
    const buttonLabel =
        backLabel ||
        overtext ||
        formatMessage({ id: 'custom-pages.node-info.back', defaultMessage: 'Back' });
    // With its own label the button no longer carries the overtext, which keeps its caption.
    const captionOvertext = !onBack || backLabel ? overtext : undefined;
    return (
        <Box>
            {onBack ? (
                // Pulled left by half a step so the label lines up with the title below it.
                <Box display="flex" marginLeft={-1}>
                    <Button
                        variant="text"
                        color="secondary"
                        startIcon={<ChevronLeftIcon />}
                        onClick={onBack}
                    >
                        {buttonLabel}
                    </Button>
                </Box>
            ) : null}
            {captionOvertext && (
                <Typography variant="caption" color="text.secondary" display="block">
                    {captionOvertext}
                </Typography>
            )}
            {/* Same title metrics as the offer page: pulled up under the back button. */}
            <Stack
                direction="row"
                alignItems="center"
                gap={1}
                mt={onBack && !captionOvertext ? -1 : 0}
                mb={0.6}
            >
                <Box flex={1} minWidth={0}>
                    <Typography variant="h6" color="text.primary" wordBreak="break-word">
                        {titleLoading ? <SkeletonShape variant="text" width="60%" /> : title}
                    </Typography>
                </Box>
                {/* The actions take their content width: an action bar is a full-width AppBar
                    that does not shrink, and would otherwise squeeze the title to zero width. */}
                {endAdornment && <Box flexShrink={0}>{endAdornment}</Box>}
            </Stack>
            <Divider />
        </Box>
    );
}
